/**
 * Fetch a shipped blueprint JSON and hydrate the three graph-state module
 * refs + animVariables so the render loop can start evaluating without the
 * editor UI being mounted.
 *
 * Mirrors what BlueprintEditor does today on first-run URL load, factored
 * out so live mode can call it directly from App.jsx.
 *
 * Shape of the blueprint JSON:
 *   {
 *     body:   { nodes: [...], edges: [...] },
 *     face:   { nodes: [...], edges: [...] },
 *     events: { eventNodes: [...], eventEdges: [...], functions?, variables? },
 *   }
 */

import { graphState } from './graphState'
import { faceGraphState } from './faceGraphState'
import { eventGraphState, notifyEventGraphChange } from './eventGraphState'
import { syncVariables } from './animVariables'
import { initSM } from './smRuntime'
import { resolveCharacterId } from './characterState'
import { getCurrentUserId } from './userScope'
import { beginHydration, currentHydrationCharacter } from './blueprintOwnership'
import { getLatestBlueprint, saveBlueprint, getBlueprintAsset, updateBlueprintAsset } from './assetManagerApi.js'
import { isEmptyBlueprintData } from './blueprintEmpty.js'
import { migrateEventGraph, EVENTS_STORAGE_VERSION, EVENTS_STORAGE_VERSION_OLD } from './blueprintMigrations.js'
import { blankBlueprintGraphs } from './blankBlueprintGraph.js'
import {
  activeKeyFor, getActiveBlueprint, setActiveBlueprint, clearActiveBlueprint, shouldDiscardCaches,
} from './activeBlueprintAsset.js'

// Pure, stateless key builder — use when you have an explicit charId. Keys are
// scoped by the authenticated user id (ENG-559) and then by charId, so two
// users on one browser never share blueprint caches.
// events bumped v6 -> v7 (Wave 2B "one node"): `beginPlay` is retyped to
// `onConstruct` at every hydration boundary (blueprintMigrations.js —
// the version constants live there so the bump is lockable under plain
// node:test). eventsOld follows one hop behind (v5 -> v6) as the one-time
// migration source; see loadEvents() in BlueprintEditor.jsx.
export function getStorageKeysFor(charId) {
  const u = `-${getCurrentUserId() ?? 'anon'}`
  const s = charId ? `-${charId}` : ''
  return {
    body:      `aos-blueprint-v6${u}${s}`,
    face:      `aos-blueprint-face-v7${u}${s}`,
    events:    `aos-blueprint-events-${EVENTS_STORAGE_VERSION}${u}${s}`,
    eventsOld: `aos-blueprint-events-${EVENTS_STORAGE_VERSION_OLD}${u}${s}`,
  }
}

// Lazy singleton — resolves character ID once and caches.
// Call resetStorageKeys() after switching characters OR after the user id is
// set, so the next getStorageKeys() call rebuilds with the right namespace.
let _keys = null
export function resetStorageKeys() { _keys = null }
export function getStorageKeys() {
  if (!_keys) {
    _keys = getStorageKeysFor(resolveCharacterId())
  }
  return _keys
}

/**
 * The per-graph localStorage caches for a character — `null` for a graph this
 * browser has no cache of. The SAME source BlueprintEditor's Save-to-Cloud
 * reads (ENG-991: a graph with no cache must carry the SERVER's copy through,
 * never a fabricated `{}`), and the one that tracks which blueprint LIBRARY
 * ROW is open for each graph, since loadBlueprintAssetGraph hydrates these
 * keys. Any writer that routes through saveBlueprintToDB must read them:
 * saveBlueprintToDB PATCHes each graph into that row, so a payload assembled
 * from anywhere else writes one row's content over another's.
 *
 * A cache that won't parse counts as ABSENT (the server copy rides through —
 * the safe direction) and says so loudly rather than passing corrupt JSON on.
 */
export function readBlueprintCaches(charId) {
  const keys = getStorageKeysFor(charId)
  const parse = (kind, key) => {
    try {
      return JSON.parse(localStorage.getItem(key) || 'null')
    } catch (e) {
      console.warn(`[readBlueprintCaches] ${charId} ${kind}: cache is not valid JSON — treating it as absent:`, e)
      return null
    }
  }
  return {
    body: parse('body', keys.body),
    face: parse('face', keys.face),
    events: parse('events', keys.events),
  }
}

function initSMs(nodes) {
  for (const n of nodes) if (n.type === 'stateMachine') initSM(n.id)
}

// Type renames (onAnimation -> onGesture, beginPlay -> onConstruct) live in
// blueprintMigrations.migrateEventGraph — one tested pass shared with the
// editor's localStorage load and file import. Applied below in
// applyBlueprintData, so BOTH load paths (shipped JSON + cloud/AAM) migrate,
// and the migrated snapshot is what reaches localStorage and the callers.

/**
 * Fetch a blueprint JSON from a URL (relative to the app root) and hydrate
 * graphState / faceGraphState / eventGraphState / variableDefs. Also mirrors
 * the data into localStorage under the editor's storage keys so reopening
 * the editor afterwards shows the same blueprint.
 *
 * @param {string} url  Path relative to public/, e.g. 'blueprints/conversational.json'
 * @param {Object} [opts]
 * @param {() => boolean} [opts.applyIf]  Checked AFTER the fetch resolves,
 *   BEFORE anything is hydrated. Returning false skips every side effect
 *   (graph refs, localStorage, variables) and resolves
 *   `{ superseded: true }`. This is how a caller vetoes a load that lost a
 *   race: the check must live here, not in the caller's `.then` — by then
 *   this function has already clobbered the runtime refs and localStorage,
 *   which is exactly how a late first-run default overwrote a character's
 *   cloud blueprint (blueprint-default-vs-cloud.spec.js) despite
 *   BlueprintEditor's guard "dropping" it.
 * @param {string|null} [opts.characterId]  Whose storage keys this load writes
 *   under, and (unless `claimOwnership` is false) who now owns the graphs.
 * @param {boolean} [opts.claimOwnership=true]  False for a load that names a
 *   character only to pick storage keys and must not re-arm it — see
 *   blueprintOwnership.beginHydration.
 * @param {(data: object) => object} [opts.transform]  Applied to the fetched
 *   document AFTER the supersede checks and BEFORE anything is hydrated, so a
 *   caller can specialise a shared template for this character (ENG-1022: the
 *   gaussian starter's branch nodes are generated from the bundle's regions).
 *   It runs here rather than in the caller because applyBlueprintData is the ONE
 *   place a blueprint reaches the graph refs and localStorage — transforming
 *   afterwards would mean the runtime and the cache disagree about what loaded.
 * @returns {Promise<object>} The parsed blueprint data
 */
// The ONE place a fetched blueprint reaches the three global graph refs.
// Both loaders route through it so the storage keys and the ownership check
// cannot drift apart between them.
//
// `characterId` picks the storage keys EXPLICITLY. The cached getStorageKeys()
// is rebuilt from whichever character is active when it is next called, and a
// switch calls resetStorageKeys() — so a response that resolves after the
// switch would write the OLD character's graph under the NEW character's keys,
// which one editor boot path then reads back.
function applyBlueprintData(characterId, data, evSrcRaw, { keepTags = false } = {}) {
  const sk = characterId ? getStorageKeysFor(characterId) : getStorageKeys()
  // ENG-1030: this call is about to REPLACE graph caches, so whatever library
  // row they were tagged as holding, they no longer do — and only for the
  // graphs actually being replaced, since the tags are per kind now. Clearing
  // here rather than in each caller is what makes it unforgettable: a path that
  // hydrates the defaults (or a ?blueprint= template) and left a stale tag
  // behind would send the next save to PATCH some other row with this content.
  //
  // keepTags is for loadBlueprintAssetGraph, the one caller that legitimately
  // owns the row it is hydrating and sets its tag immediately after.
  if (!keepTags) {
    const charForTag = characterId ?? resolveCharacterId()
    const uid = getCurrentUserId()
    if (data.body) clearActiveBlueprint(activeKeyFor(uid, charForTag, 'body'))
    if (data.face) clearActiveBlueprint(activeKeyFor(uid, charForTag, 'face'))
    if (evSrcRaw) clearActiveBlueprint(activeKeyFor(uid, charForTag, 'events'))
  }
  // Migrate ONCE at the boundary: what hits localStorage, the runtime refs
  // and the caller (BlueprintEditor's React state) is the same migrated shape.
  const evSrc = migrateEventGraph(evSrcRaw)

  if (data.body) {
    localStorage.setItem(sk.body, JSON.stringify(data.body))
    graphState.nodes = data.body.nodes ?? []
    graphState.edges = data.body.edges ?? []
    initSMs(graphState.nodes)
  }

  if (data.face) {
    localStorage.setItem(sk.face, JSON.stringify(data.face))
    faceGraphState.nodes = data.face.nodes ?? []
    faceGraphState.edges = data.face.edges ?? []
    initSMs(faceGraphState.nodes)
  }

  if (evSrc) {
    localStorage.setItem(sk.events, JSON.stringify(evSrc))
    eventGraphState.nodes = evSrc.eventNodes ?? []
    eventGraphState.edges = evSrc.eventEdges ?? []
    eventGraphState.functions = evSrc.functions ?? {}
    notifyEventGraphChange()
  }

  const importedVars = evSrc?.variables ?? data.body?.variables ?? data.face?.variables
  if (importedVars?.length) syncVariables(importedVars)
  return { importedVars: importedVars ?? null, evSrc }
}

export async function loadBlueprint(url, { applyIf, characterId = null, claimOwnership = true, transform } = {}) {
  const target = '/' + url.replace(/^\//, '')
  // Claim ownership BEFORE the await, so a later load for another character
  // supersedes this one rather than racing it (blueprintOwnership).
  const hydration = beginHydration(characterId, { claim: claimOwnership })
  const res = await fetch(target)
  if (!res.ok) throw new Error(`loadBlueprint ${target}: ${res.status} ${res.statusText}`)
  const raw = await res.json()

  // `applyIf` is a SEPARATE question from ownership — the first-run default asks
  // "has the cloud graph already landed?" — so both are checked.
  if (applyIf && !applyIf()) {
    return { data: raw, evSrc: null, variables: null, superseded: true }
  }
  if (!hydration.stillCurrent()) {
    console.warn(`[loadBlueprint] ${target}: superseded — "${currentHydrationCharacter()}" is loading now`)
    return { data: raw, evSrc: null, variables: null, superseded: true }
  }

  // Specialise the shared template for this character (see `transform` above).
  // After the supersede checks, so a load that lost the race does no work.
  const data = transform ? transform(raw) : raw

  const evSrcRaw = data.events
    ?? (data.face?.eventNodes?.length ? { eventNodes: data.face.eventNodes, eventEdges: data.face.eventEdges ?? [] } : null)
    ?? (data.body?.eventNodes?.length ? { eventNodes: data.body.eventNodes, eventEdges: data.body.eventEdges ?? [] } : null)
  const { importedVars, evSrc } = applyBlueprintData(characterId, data, evSrcRaw)

  // Return both the raw data and the resolved (MIGRATED) event/variable
  // sources so the BlueprintEditor can sync its React state without
  // re-deriving them.
  return { data, evSrc, variables: importedVars }
}

/**
 * Load a blueprint from the database for a character. Falls back to null
 * if the backend is not ready (404) or if the request fails.
 *
 * If found, writes the blueprint into localStorage so that subsequent edits
 * auto-save there (existing behavior).
 *
 * @param {string} characterId - The character's ID/slug
 * @returns {Promise<{ data: object, evSrc: object, variables: array } | null>}
 */
export async function loadBlueprintFromDB(characterId) {
  // Claim ownership BEFORE the await — see blueprintOwnership.
  const hydration = beginHydration(characterId)
  try {
    const data = await getLatestBlueprint(characterId)

    // Superseded is NOT "no blueprint": callers turn null into a loud
    // "not found on the server", so it gets its own marker.
    if (!hydration.stillCurrent()) {
      console.warn(`[loadBlueprintFromDB] ${characterId}: superseded — "${currentHydrationCharacter()}" is loading now`)
      return { data, evSrc: null, variables: null, superseded: true }
    }

    // An all-empty blueprint row (0 nodes in body, face AND events) carries no
    // information and must be treated as "no blueprint" — hydrating it would
    // CLOBBER whatever the graph refs already hold (e.g. a ?blueprint= URL
    // load) with nothing, freezing the avatar in bind pose. Such rows exist:
    // the pre-fix Sync-to-Cloud flow fabricated one for myra on staging on
    // 2026-07-01, which broke every main-CI animation spec. Loud, not silent.
    if (isEmptyBlueprintData(data)) {
      console.warn(`[loadBlueprintFromDB] ${characterId}: server blueprint is EMPTY (0 nodes in all graphs) — ignoring it. The row was likely fabricated by a pre-fix Sync to Cloud with no local edits; re-save from a browser that has the real graphs.`)
      return null
    }

    const { importedVars, evSrc } = applyBlueprintData(characterId, data, data.events ?? {})

    return { data, evSrc, variables: importedVars }
  } catch (e) {
    // 404 (no blueprint yet) or network error — both are non-fatal
    if (!e.message?.includes('404')) {
      console.warn(`[loadBlueprintFromDB] ${characterId}: ${e.message}`)
    }
    return null
  }
}

/**
 * Load one NAMED blueprint from the character's library into the editor
 * (ENG-1030). This is what the content browser's Load action calls.
 *
 * Two things separate it from loadBlueprintFromDB, which loads whichever
 * blueprint is the DEFAULT:
 *
 * 1. It is addressed by blueprint id, so it can load a blueprint the character
 *    does not currently load.
 * 2. It RETAGS the graph caches (activeBlueprintAsset). Loading blueprint B
 *    while the caches hold blueprint A's graphs is the one way this feature can
 *    destroy content — a later save would write A into B, populated and
 *    plausible, past every empty-graph guard. applyBlueprintData overwrites all
 *    three caches from the fetched blueprint, so the tag is what makes the
 *    overwrite provable rather than assumed.
 *
 * An empty blueprint is refused for the same reason the DB loader refuses one:
 * hydrating nothing over working graph refs freezes the avatar in bind pose.
 *
 * @param {string} assetId the blueprint's id
 * @param {string} characterId the character it belongs to (for the storage keys
 *   and the ownership race guard)
 * @returns {Promise<{ data, evSrc, variables } | null>} null when the blueprint
 *   carries no graphs at all; throws when the fetch itself fails, so the caller
 *   can report it rather than silently keeping the old graphs.
 */
export async function loadBlueprintAssetGraph(assetId, characterId, graphKind) {
  const hydration = beginHydration(characterId)
  const row = await getBlueprintAsset(assetId)
  const kind = graphKind ?? row?.kind
  if (!kind) throw new Error(`blueprint ${assetId} does not say which graph it is`)

  if (!hydration.stillCurrent()) {
    console.warn(`[loadBlueprintAssetGraph] ${assetId}: superseded — "${currentHydrationCharacter()}" is loading now`)
    return { data: row, evSrc: null, variables: null, superseded: true }
  }

  // Shape the one graph into the document applyBlueprintData consumes, so a
  // library row and a whole blueprint reach the graph refs through the SAME
  // hydration point rather than two that can drift.
  const graph = row?.graph ?? {}
  const data = kind === 'events' ? { events: graph } : { [kind]: graph }

  if (isEmptyGraphDocument(kind, graph)) {
    console.warn(`[loadBlueprintAssetGraph] ${assetId}: the ${kind} graph has 0 nodes — refusing to load it over the current one, which would leave the character with nothing driving that surface.`)
    return null
  }

  const { importedVars, evSrc } = applyBlueprintData(characterId, data, kind === 'events' ? graph : null, { keepTags: true })
  // AFTER hydration, never before: the tag has to describe what is actually in
  // the cache, so a failed hydrate must not leave it claiming otherwise.
  setActiveBlueprint(activeKeyFor(getCurrentUserId(), characterId, kind), assetId)
  return { data, evSrc, variables: importedVars, kind }
}

// isEmptyGraphDocument judges one graph by its own node collection — `nodes` for
// body/face, `eventNodes` for events. blueprintEmpty.js judges a whole
// three-graph document and cannot answer for one.
function isEmptyGraphDocument(kind, graph) {
  if (!graph || typeof graph !== 'object') return true
  const key = kind === 'events' ? 'eventNodes' : 'nodes'
  return (graph[key]?.length ?? 0) === 0
}

/**
 * Whether loading `assetId` would be loading something other than what the
 * graph caches hold — i.e. whether an unsaved draft is about to be discarded.
 * The caller (UI) uses this to confirm first; it is a question, not an action.
 */
export function blueprintCachesAreForeign(assetId, characterId, graphKind) {
  const record = getActiveBlueprint(activeKeyFor(getCurrentUserId(), characterId, graphKind))
  return shouldDiscardCaches(record, assetId)
}

/**
 * The library blueprint this browser is editing, or null when it has only ever
 * loaded the character's default (the ordinary case).
 */
export function getActiveBlueprintId(characterId, graphKind) {
  return getActiveBlueprint(activeKeyFor(getCurrentUserId(), characterId, graphKind)).activeAssetId
}

/**
 * Decide where the EDITOR reads a character's blueprint on open / character
 * switch. Cloud is authoritative (ENG-797/798): localStorage is deliberately
 * NOT an input, so a stale local cache can never become the boot source and
 * shadow the correct server graph (the bug that forced "Load from Cloud every
 * time"). An explicit ?blueprint= URL still overrides for reset/testing.
 *
 * @param {Object} config
 * @param {boolean} config.hasBlueprintParam - is a ?blueprint= URL present
 * @returns {{ kind: 'url' | 'db' }}
 */
export function resolveEditorBlueprintSource({ hasBlueprintParam }) {
  return hasBlueprintParam ? { kind: 'url' } : { kind: 'db' }
}

/**
 * Write empty blueprint state for a character into localStorage so the editor
 * treats the character as intentionally blank rather than falling back to
 * the conversational.json default.
 *
 * "Empty" means "no user authoring", not "no nodes": body/face carry the
 * mandatory non-deletable finalPose, and events is seeded with the Orbit
 * Camera Rig group so a new character has working camera controls out of the
 * box instead of a dead camera + warning. The rig is universal (every renderer
 * uses it); renderer-specific clusters like the OGS splat nodes are NOT seeded
 * here — a splat character gets them from its STARTER template, whose branch
 * nodes are generated per bundle region (starterBlueprints.js).
 *
 * This used to say splat characters "pull those from isaac.json". They could
 * not: that file has no plugin.ogs.renderer node and never had one, so the
 * render phase settled at 'three' and nothing rendered (ENG-1022). A stated
 * contract that nothing enforced is what kept the gap invisible.
 *
 * NOTE this writes localStorage ONLY — it is not a server row. Nothing in the
 * system mints one; a blueprint exists only once a person saves. That is why a
 * character authored on one machine renders there and nowhere else.
 *
 * The graphs come from blankBlueprintGraph.js (one definition, shared with
 * the content browser's New → Blueprint → <graph>), whose event graph carries
 * the orbit rig from CAMERA_RIG_TEMPLATES.orbit (single source of truth,
 * same shape the palette insert and server-side backfill produce), not a
 * hardcoded node.
 *
 * @param {string} charId - The character's ID/slug
 */
export function writeEmptyBlueprintsForCharacter(charId) {
  const sk = getStorageKeysFor(charId)
  const blank = blankBlueprintGraphs()
  localStorage.setItem(sk.body,   JSON.stringify(blank.body))
  localStorage.setItem(sk.face,   JSON.stringify(blank.face))
  localStorage.setItem(sk.events, JSON.stringify(blank.events))
}

/**
 * Save a blueprint to the database for a character. Fire-and-forget:
 * does not throw on failure, gracefully ignores 404 / network errors
 * (backend may not be ready yet).
 *
 * @param {string} characterId - The character's ID/slug
 * @param {object} blueprintData - { body, face, events } from the current graphs
 * @returns {Promise<void>}
 */
export async function saveBlueprintToDB(characterId, blueprintData) {
  // ENG-1030: a library row is ONE graph, so a save fans out — each graph goes
  // to the row the editor opened it from, and only the graphs with no such row
  // fall through to the singleton endpoint (which resolves that kind's default
  // server-side, exactly as before).
  //
  // Without this, edits made to one row would land on the character's default,
  // which is data loss wearing a success message.
  const uid = getCurrentUserId()
  const routed = []       // [{ kind, assetId, graph }]
  const unrouted = {}     // { kind: graph } → singleton
  for (const kind of ['body', 'face', 'events']) {
    const graph = blueprintData?.[kind]
    if (graph === undefined) continue
    const assetId = getActiveBlueprintId(characterId, kind)
    if (assetId) routed.push({ kind, assetId, graph })
    else unrouted[kind] = graph
  }

  let ok = true

  for (const { kind, assetId, graph } of routed) {
    console.log(`[saveBlueprintToDB] PATCH /api/blueprint-assets/${assetId} (${kind})`)
    try {
      // No `allow_empty`: that is the server's deliberate-wipe override, and a
      // client must never volunteer it. Emptying a graph the stored row already
      // has empty needs no override, and emptying a populated one is a decision
      // the user has to make explicitly — not one the save path assumes.
      await updateBlueprintAsset(assetId, { graph })
      console.log(`[saveBlueprintToDB] ${characterId}: OK (${kind} → ${assetId})`)
    } catch (e) {
      console.warn(`[saveBlueprintToDB] ${characterId} ${kind} → ${assetId}: ${e.message}`)
      ok = false
    }
  }

  if (Object.keys(unrouted).length > 0) {
    console.log(`[saveBlueprintToDB] POST /api/characters/${characterId}/blueprint (${Object.keys(unrouted).join(', ')})`)
    try {
      // The singleton replaces the whole document, so a partial payload would
      // empty the graphs it omits. Send what the editor holds for every kind —
      // mergeBlueprintForSave has already filled any gap with the server's own
      // copy — and let the server judge it against the stored row.
      await saveBlueprint(characterId, blueprintData)
      console.log(`[saveBlueprintToDB] ${characterId}: OK`)
    } catch (e) {
      console.warn(`[saveBlueprintToDB] ${characterId}: ${e.message}`)
      ok = false
    }
  }

  return ok
}
